// user-notifications.service.ts
import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { BehaviorSubject, EMPTY, Subject } from 'rxjs';
import { catchError, finalize, map, tap } from 'rxjs/operators';
import { environment } from 'environments/environment';
import { skipLoaderHeaders } from 'app/core/interceptors/loader.interceptor';
import {
  UserNotification,
  UserNotificationPaginated,
  UserNotificationUnreadCount,
} from '@interfaces/Reminder';
import { ApiResponse } from '@interfaces/httpResponses';
import { NotificationService } from './handlers/notification.service';

@Injectable({ providedIn: 'root' })
export class UserNotificationsService {
  private baseUrl = `${environment.apiUrl}user/notifications`;

  private _isLoading$ = new BehaviorSubject<boolean>(false);
  isLoading$ = this._isLoading$.asObservable();

  private notificationsSubject = new BehaviorSubject<UserNotification[]>([]);
  notifications$ = this.notificationsSubject.asObservable();

  private unreadCountSubject = new BehaviorSubject<number>(0);
  unreadCount$ = this.unreadCountSubject.asObservable();

  private allNotificationsSubject = new Subject<UserNotificationPaginated>();
  allNotifications$ = this.allNotificationsSubject.asObservable();

  // recuerda la última query paginada para poder refrescarla tras mutaciones
  private lastAllQuery?: { page: number; limit: number; notified?: boolean };

  constructor(
    private http: HttpClient,
    private notificationService: NotificationService,
  ) {}

  private refreshAfterMutation(): void {
    // refresca badge
    this.loadNotifications();
    this.loadUnreadCount();
    // refresca tabla paginada con la última query usada (si existe)
    if (this.lastAllQuery) {
      const { page, limit, notified } = this.lastAllQuery;
      this.loadAllNotifications(page, limit, notified);
    }
  }

  loadUnreadCount(): void {
    this.http
      .get<ApiResponse<UserNotificationUnreadCount>>(
        `${this.baseUrl}/unread-count`,
        { headers: skipLoaderHeaders() },
      )
      .pipe(
        tap((res) => this.unreadCountSubject.next(res.payload.count)),
        catchError((err) => {
          this.notificationService.showToastError(err);
          return EMPTY;
        }),
      )
      .subscribe();
  }

  loadNotifications(): void {
    this._isLoading$.next(true);
    this.http
      .get<ApiResponse<UserNotification[]>>(`${this.baseUrl}`, {
        headers: skipLoaderHeaders(),
      })
      .pipe(
        tap((res) => this.notificationsSubject.next(res.payload)),
        catchError((err) => {
          this.notificationService.showToastError(err);
          return EMPTY;
        }),
        finalize(() => this._isLoading$.next(false)),
      )
      .subscribe();
  }

  loadAllNotifications(page = 1, limit = 10, notified?: boolean): void {
    this.lastAllQuery = { page, limit, notified };

    this._isLoading$.next(true);
    let params = new HttpParams()
      .set('page', String(page))
      .set('limit', String(limit));
    if (notified !== undefined)
      params = params.set('notified', String(notified));

    this.http
      .get<ApiResponse<UserNotificationPaginated>>(`${this.baseUrl}/all`, {
        params,
        headers: skipLoaderHeaders(),
      })
      .pipe(
        map((res) => res.payload),
        tap((payload) => this.allNotificationsSubject.next(payload)),
        catchError((err) => {
          this.notificationService.showToastError(err);
          return EMPTY;
        }),
        finalize(() => this._isLoading$.next(false)),
      )
      .subscribe();
  }

  markAsRead(notificationId: string): void {
    this._isLoading$.next(true);
    this.http
      .put<{ status: string; payload: string }>(
        `${this.baseUrl}/notified/${notificationId}`,
        {},
        { headers: skipLoaderHeaders() },
      )
      .pipe(
        tap((res) => {
          this.notificationsSubject.next(
            this.notificationsSubject.value.map((notification) =>
              notification.id === notificationId
                ? { ...notification, read: true }
                : notification,
            ),
          );
          this.notificationService.showTotast(res.payload);
          this.refreshAfterMutation();
        }),
        catchError((err) => {
          this.notificationService.showToastError(err);
          return EMPTY;
        }),
        finalize(() => this._isLoading$.next(false)),
      )
      .subscribe();
  }

  markAllAsRead(): void {
    this._isLoading$.next(true);
    this.http
      .put<ApiResponse<UserNotificationUnreadCount>>(
        `${this.baseUrl}/read-all`,
        {},
        { headers: skipLoaderHeaders() },
      )
      .pipe(
        tap(() => this.refreshAfterMutation()),
        catchError((err) => {
          this.notificationService.showToastError(err);
          return EMPTY;
        }),
        finalize(() => this._isLoading$.next(false)),
      )
      .subscribe();
  }
}
